import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Typography, Empty } from 'antd';
import moment from 'moment';

const { Text } = Typography;

const TimelineContainer = styled.div`
	position: relative;
	padding-left: 24px;
`;

const TimelineLine = styled.div`
	position: absolute;
	left: 8px;
	top: 0;
	bottom: 0;
	width: 2px;
	background-color: #1890ff;
`;

const TimelineItem = styled.div`
	position: relative;
	margin-bottom: 24px;
	&:last-child {
		margin-bottom: 0;
	}
`;

const TimelineDot = styled.div`
	position: absolute;
	left: -20px;
	top: 8px;
	width: 12px;
	height: 12px;
	border-radius: 50%;
	background-color: #1890ff;
	border: 2px solid #fff;
	z-index: 1;
`;

const StatusTag = styled.div`
	display: inline-block;
	padding: 4px 12px;
	border-radius: 4px;
	font-size: 14px;
	font-weight: 500;
	margin-bottom: 8px;
	background-color: ${(props) => {
		if (props.color === 'green') return '#f6ffed';
		if (props.color === 'red') return '#fff1f0';
		return '#f0f0f0'; // gray/blue default
	}};
	color: ${(props) => {
		if (props.color === 'green') return '#52c41a';
		if (props.color === 'red') return '#ff4d4f';
		return '#595959'; // gray/blue default
	}};
`;

const DescriptionText = styled(Text)`
	display: block;
	font-size: 14px;
	color: #595959;
	margin-bottom: 4px;
	line-height: 1.5;
`;

const TimestampText = styled(Text)`
	display: block;
	font-size: 12px;
	color: #8c8c8c;
`;

function StatusHistory({ historyData = [] }) {
	if (!historyData || historyData.length === 0) {
		return (
			<Empty
				description={<Text type="secondary">No status history available</Text>}
				style={{ padding: '40px 0' }}
			/>
		);
	}

	// Map status_color from API to visual colors
	const getStatusColor = (statusColor, eventDisplay) => {
		// Handle specific events
		if (eventDisplay?.toLowerCase().includes('rejected')) {
			return 'red';
		}
		if (
			eventDisplay?.toLowerCase().includes('approved') ||
			eventDisplay?.toLowerCase().includes('created') ||
			eventDisplay?.toLowerCase().includes('paid') ||
			eventDisplay?.toLowerCase().includes('marked')
		) {
			return 'green';
		}
		// Default mapping: blue -> gray
		if (statusColor === 'blue') {
			return 'gray';
		}
		return statusColor || 'gray';
	};

	// Convert UTC timestamp to IST and format for display
	const formatTimestampToIST = (timestamp) => {
		if (!timestamp) return '-';

		// Parse as UTC and add 330 minutes (5 hours 30 minutes) to convert to IST
		const utcTime = moment.utc(timestamp);
		if (!utcTime.isValid()) return timestamp;

		const istTime = utcTime.add(330, 'minutes');
		return istTime.format('dddd, DD MMMM YYYY | h:mm A');
	};

	return (
		<TimelineContainer>
			<TimelineLine />
			{historyData.map((item) => {
				const statusColor = getStatusColor(item.status_color, item.event_display);
				// Use timestamp + event as unique key since timestamp alone might not be unique
				const uniqueKey = `${item.timestamp || ''}-${item.event || ''}-${
					item.user_name || ''
				}`;
				return (
					<TimelineItem key={uniqueKey}>
						<TimelineDot />
						<StatusTag color={statusColor}>
							{item.event_display || item.event}
						</StatusTag>
						<DescriptionText>{item.display_text || '-'}</DescriptionText>
						<TimestampText>{formatTimestampToIST(item.timestamp)}</TimestampText>
					</TimelineItem>
				);
			})}
		</TimelineContainer>
	);
}

StatusHistory.propTypes = {
	historyData: PropTypes.arrayOf(
		PropTypes.shape({
			display_text: PropTypes.string,
			event: PropTypes.string,
			event_display: PropTypes.string,
			status_color: PropTypes.string,
			timestamp: PropTypes.string,
			timestamp_display: PropTypes.string,
			user_name: PropTypes.string,
			user_type: PropTypes.string,
			user_type_display: PropTypes.string,
		})
	),
};

export default StatusHistory;
